import type { IdentifyTraits } from '@rudderstack/analytics-js';
import { RudderAnalytics } from '@rudderstack/analytics-js';
import { useStatsigClient } from '@statsig/react-bindings';
import { useSessionValue } from 'appLayer/auth';
import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { config } from 'shared/config';

interface RudderAnalyticsContextType {
  analytics: RudderAnalytics | undefined;
}

const RudderAnalyticsContext = createContext<RudderAnalyticsContextType | undefined>(undefined);

export const useRudderAnalytics = () => {
  const context = useContext(RudderAnalyticsContext);
  if (!context) {
    throw new Error('useRudderAnalytics must be used within a RudderAnalyticsProvider');
  }
  return context.analytics;
};

interface RudderAnalyticsProviderProps {
  children: ReactNode;
}

export const RudderAnalyticsProvider = ({ children }: RudderAnalyticsProviderProps) => {
  const [analytics, setAnalytics] = useState<RudderAnalytics | undefined>(undefined);
  const session = useSessionValue();
  const { client } = useStatsigClient();
  const stableId = client.getContext().stableID;
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!analytics) {
      const initialize = async () => {
        const analyticsInstance = new RudderAnalytics();

        const isProduction = config.NEXT_PUBLIC_VERCEL_ENV === 'production';

        analyticsInstance.load(
          config.NEXT_PUBLIC_RUDDERSTACK_WRITE_KEY,
          config.NEXT_PUBLIC_RUDDERSTACK_DATA_PLANE_URL,
          isProduction
            ? {
                useServerSideCookies: true,
                dataServiceEndpoint: '/api/rsaRequest'
              }
            : {}
        );

        setAnalytics(analyticsInstance);
      };

      initialize().catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
    }
  }, [analytics]);

  // Handle user identification with reset on user change
  useEffect(() => {
    if (!analytics) return;

    const currentUserId = session?.customer?.id?.toString() ?? null;

    // Reset if user has changed (login/logout)
    if (lastUserIdRef.current !== currentUserId) {
      analytics.reset();
      lastUserIdRef.current = currentUserId;
    }

    // Build traits for identification
    const traits: IdentifyTraits = {};

    if (session?.customer?.id) {
      traits.email = session.customer.email ?? undefined;
      traits.firstName = session.customer.firstName ?? undefined;
      traits.lastName = session.customer.lastName ?? undefined;
    }

    if (stableId) {
      traits.externalId = [{ id: stableId, type: 'statsigStableId' }];
    }

    // Identify user with traits
    if (Object.keys(traits).length > 0) {
      if (currentUserId) {
        analytics.identify(currentUserId, traits);
      } else {
        analytics.identify(traits);
      }
    }
  }, [analytics, session, stableId]);

  const value = useMemo(() => ({ analytics }), [analytics]);

  return (
    <RudderAnalyticsContext.Provider value={value}>{children}</RudderAnalyticsContext.Provider>
  );
};
